import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Shield, FileText, MessageSquare, ArrowRight, Star } from 'lucide-react';
import { mockServices, mockTestimonials, mockFAQs } from '../mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';

const Home = () => {
  const { language } = useLanguage();

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "DépôtSecure",
    "description": "Service d'aide et modèles pour sécuriser vos dépôts de garantie Airbnb",
    "url": "https://www.depotsecure.net",
    "logo": "https://www.depotsecure.net/logo.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "email": "contact@depotsecure.net",
      "availableLanguage": ["French", "English"]
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "EUR",
      "lowPrice": "147",
      "highPrice": "497"
    }
  };

  const content = {
    fr: {
      hero: {
        title: 'Arrêtez de perdre votre',
        titleHighlight: 'dépôt de garantie',
        subtitle: 'Propriétaire de location saisonnière : méthode pas à pas + modèles prêts à envoyer pour sécuriser vos locations et récupérer votre argent en cas de litige.',
        cta: 'Découvrir nos solutions',
        cta2: 'Voir les tarifs'
      },
      problems: {
        title: 'Vous reconnaissez-vous ?',
        list: [
          'Le locataire conteste systématiquement chaque retenue',
          'Vous ne savez pas quoi écrire dans le bail ou la mise en demeure',
          'Vous avez peur de ne pas respecter la loi et de perdre au tribunal',
          'Vous avez déjà perdu des centaines d\'euros sur des dommages non remboursés',
          'Vous passez des heures à chercher des informations légales contradictoires'
        ]
      },
      solution: {
        title: 'La solution simple et efficace',
        subtitle: 'Tout ce dont vous avez besoin pour sécuriser votre dépôt de garantie et récupérer votre argent',
        features: [
          {
            icon: FileText,
            title: 'Modèles juridiques',
            description: 'Contrats, courriers AR, mises en demeure conformes au Code civil'
          },
          {
            icon: Shield,
            title: 'Protection maximale',
            description: 'Check-lists complètes pour des états des lieux inattaquables'
          },
          {
            icon: MessageSquare,
            title: 'Accompagnement expert',
            description: 'Support personnalisé pour résoudre vos litiges rapidement'
          }
        ]
      },
      stats: {
        title: 'Des résultats prouvés',
        items: [
          { value: '94%', label: 'Taux de récupération' },
          { value: '2-4', label: 'Semaines en moyenne' },
          { value: '750+', label: 'Propriétaires aidés' }
        ]
      },
      services: {
        title: 'Choisissez votre solution',
        subtitle: 'Des offres adaptées à chaque situation',
        cta: 'Je commence'
      },
      testimonials: {
        title: 'Ils ont récupéré leur dépôt',
        subtitle: 'Des centaines de propriétaires nous font confiance'
      },
      faq: {
        title: 'Questions fréquentes',
        subtitle: 'Tout ce que vous devez savoir avant de commencer'
      },
      finalCta: {
        title: 'Prêt à sécuriser vos dépôts ?',
        subtitle: 'Rejoignez des centaines de propriétaires qui protègent leurs revenus locatifs',
        cta: 'Contactez-nous maintenant'
      }
    },
    en: {
      hero: {
        title: 'Stop losing your',
        titleHighlight: 'security deposit',
        subtitle: 'Short-term rental owner: step-by-step method + ready-to-send templates to secure your rentals and recover your money in case of dispute.',
        cta: 'Discover our solutions',
        cta2: 'See pricing'
      },
      problems: {
        title: 'Do you recognize yourself?',
        list: [
          'Tenant systematically contests every deduction',
          'You don\'t know what to write in the lease or formal notice',
          'You\'re afraid of not complying with the law and losing in court',
          'You\'ve already lost hundreds of euros on unreimbursed damages',
          'You spend hours searching for contradictory legal information'
        ]
      },
      solution: {
        title: 'The simple and effective solution',
        subtitle: 'Everything you need to secure your security deposit and recover your money',
        features: [
          {
            icon: FileText,
            title: 'Legal templates',
            description: 'Contracts, registered letters, formal notices compliant with Civil Code'
          },
          {
            icon: Shield,
            title: 'Maximum protection',
            description: 'Complete checklists for bulletproof property inspections'
          },
          {
            icon: MessageSquare,
            title: 'Expert support',
            description: 'Personalized support to resolve your disputes quickly'
          }
        ]
      },
      stats: {
        title: 'Proven results',
        items: [
          { value: '94%', label: 'Recovery rate' },
          { value: '2-4', label: 'Weeks on average' },
          { value: '750+', label: 'Owners helped' }
        ]
      },
      services: {
        title: 'Choose your solution',
        subtitle: 'Offers adapted to every situation',
        cta: 'Get started'
      },
      testimonials: {
        title: 'They recovered their deposit',
        subtitle: 'Hundreds of owners trust us'
      },
      faq: {
        title: 'Frequently asked questions',
        subtitle: 'Everything you need to know before starting'
      },
      finalCta: {
        title: 'Ready to secure your deposits?',
        subtitle: 'Join hundreds of owners protecting their rental income',
        cta: 'Contact us now'
      }
    }
  };

  const t = content[language];

  return (
    <>
      <SEO
        title={language === 'fr' ? "Accueil - Sécurisez vos dépôts Airbnb" : "Home - Secure your Airbnb deposits"}
        description={language === 'fr' 
          ? "DépôtSecure vous aide à sécuriser vos dépôts de garantie Airbnb avec des modèles professionnels, audits express et accompagnement en cas de litige. Conformité légale garantie."
          : "DépôtSecure helps you secure your Airbnb security deposits with professional templates, express audits and litigation support. Legal compliance guaranteed."}
        keywords="airbnb, dépôt de garantie, location saisonnière, protection propriétaire, modèles bail, audit express, litige locataire"
        url="/"
        schema={schema}
        language={language}
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1544984243-ec57ea16fe25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHx2YWNhdGlvbiUyMHJlbnRhbHxlbnwwfHx8fDE3NjQ4NDc5NzZ8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-blue-900 text-white hover:bg-blue-800">
              {language === 'fr' ? 'Pour propriétaires Airbnb & Booking' : 'For Airbnb & Booking owners'}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              {t.hero.title}{' '}
              <span className="text-blue-900">{t.hero.titleHighlight}</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="bg-blue-900 hover:bg-blue-800 text-white shadow-lg hover:shadow-xl transition-all"
              >
                <Link to="/services">
                  {t.hero.cta}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-blue-900 text-blue-900 hover:bg-blue-50"
              >
                <Link to="/services">{t.hero.cta2}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center">
              {t.problems.title}
            </h2>
            <div className="space-y-4">
              {t.problems.list.map((problem, index) => (
                <Card key={index} className="border-l-4 border-l-red-500 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-1">
                      <span className="text-red-600 font-bold text-sm">×</span>
                    </div>
                    <p className="text-slate-700 flex-1">{problem}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t.solution.title}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t.solution.subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.solution.features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                  <CardContent className="p-8">
                    <div className="bg-gradient-to-br from-blue-900 to-blue-700 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            {t.stats.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.stats.items.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold mb-2">{stat.value}</div>
                <div className="text-blue-200 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t.services.title}
            </h2>
            <p className="text-lg text-slate-600">{t.services.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockServices.map((service) => (
              <Card
                key={service.id}
                className={`relative overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 ${
                  service.popular ? 'border-2 border-blue-900' : ''
                }`}
              >
                {service.popular && (
                  <div className="absolute top-0 right-0 bg-blue-900 text-white px-4 py-1 text-sm font-semibold">
                    {language === 'fr' ? 'Populaire' : 'Popular'}
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {service.title[language]}
                  </h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-blue-900">€{service.price}</span>
                  </div>
                  <p className="text-slate-600 mb-6">{service.description[language]}</p>
                  <ul className="space-y-3 mb-8">
                    {service.features[language].map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/payment?service=${service.id}`} className="block w-full">
                    <Button className="w-full bg-blue-900 hover:bg-blue-800 text-white">
                      {t.services.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t.testimonials.title}
            </h2>
            <p className="text-lg text-slate-600">{t.testimonials.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 italic">"{testimonial.text[language]}"</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-500">{testimonial.location[language]}</div>
                    </div>
                    {testimonial.amount && (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                        {testimonial.amount}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t.faq.title}
            </h2>
            <p className="text-lg text-slate-600">{t.faq.subtitle}</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {mockFAQs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="border border-slate-200 rounded-lg px-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-blue-900">
                  {faq.question[language]}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pt-2">
                  {faq.answer[language]}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-900 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1715985160053-d339e8b6eb94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBhaXJibmIlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NjQ4NDc5NjV8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            {t.finalCta.title}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t.finalCta.subtitle}
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-blue-900 hover:bg-slate-100 shadow-xl hover:shadow-2xl transition-all"
          >
            <Link to="/contact">
              {t.finalCta.cta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;
